import { useState } from 'react';
import { PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface AddDomainFormProps {
  onAddDomain: (domain: string) => void;
  domainsCount: number;
  domainsLimit: number;
}

const AddDomainForm: React.FC<AddDomainFormProps> = ({ 
  onAddDomain, 
  domainsCount, 
  domainsLimit 
}) => {
  const [domain, setDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!domain) {
      toast.error('Please enter a domain name');
      return;
    }

    // Accept both full URLs and domain names for future uptime monitoring support
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(:\d+)?(\/.*)?$/i;
    if (!urlPattern.test(domain)) {
      toast.error('Please enter a valid domain name or URL');
      return;
    }

    // Check if domain limit is reached
    if (domainsCount >= domainsLimit) {
      toast.error(`You've reached your domain limit (${domainsLimit}). Upgrade your plan to add more domains.`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onAddDomain(domain);
      setDomain('');
      toast.success(`Domain "${domain}" added successfully`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to add domain');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="m-0 p-0">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-grow">
          <label htmlFor="domain" className="sr-only">
            Domain Name
          </label>
          <input
            type="text"
            id="domain"
            name="domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-base"
            disabled={isSubmitting || domainsCount >= domainsLimit}
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting || domainsCount >= domainsLimit}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
            (isSubmitting || domainsCount >= domainsLimit) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          {isSubmitting ? 'Adding...' : 'Add Domain'}
        </button>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {domainsLimit === Infinity ? (
          <span>You can add unlimited domains with your current plan.</span>
        ) : (
          <span>
            {domainsCount} of {domainsLimit} domains used ({((domainsCount / domainsLimit) * 100).toFixed(1)}%)
          </span>
        )}
      </div>
    </form>
  );
};

export default AddDomainForm;